import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Tooltip, Progress, Select, Typography, Empty, Descriptions, Statistic, Row, Col, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, ReloadOutlined, SyncOutlined, LinkOutlined, DisconnectOutlined, KeyOutlined, SafetyCertificateOutlined, BarChartOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';

import { adminPath } from '../../../shared/config/paths';
import { formatBytes } from '../../../shared/utils/format';

const Domains = () => {
    const [domains, setDomains] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDomain, setEditingDomain] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [submitting, setSubmitting] = useState(false);
    const [syncSubmitting, setSyncSubmitting] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();

    // Sync states
    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncingDomain, setSyncingDomain] = useState(null);
    const [syncStatus, setSyncStatus] = useState({});
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [syncForm] = Form.useForm();

    // DNS/DKIM states
    const [dnsModalVisible, setDnsModalVisible] = useState(false);
    const [dnsDomain, setDnsDomain] = useState(null);
    const [dnsRecords, setDnsRecords] = useState([]);
    const [dnsLoading, setDnsLoading] = useState(false);
    const [dkimLoading, setDkimLoading] = useState(false);

    // Domain stats states
    const [statsModalVisible, setStatsModalVisible] = useState(false);
    const [statsDomain, setStatsDomain] = useState(null);
    const [domainStats, setDomainStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                const defaultCluster = list.find(c => c.is_default);
                if (defaultCluster) {
                    setSelectedCluster(defaultCluster.id);
                } else if (list.length > 0) {
                    setSelectedCluster(list[0].id);
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchDomains = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/domains', {
                params: { cluster_id: selectedCluster, page, per_page: pageSize }
            });
            if (res.data.code === 0) {
                setDomains(res.data.data.list || []);
                setPagination({
                    current: res.data.data.page,
                    pageSize: res.data.data.per_page,
                    total: res.data.data.total,
                });
            }
        } catch (error) {
            message.error('获取域名列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchDomains();
        }
    }, [selectedCluster]);

    const handleAdd = () => {
        setEditingDomain(null);
        form.resetFields();
        form.setFieldsValue({
            active: true,
            quota_bytes: 10737418240,
            quota_messages: 100000,
            max_users: 100,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingDomain(record);
        form.setFieldsValue({
            ...record,
            quota_bytes_gb: record.quota_bytes / (1024 * 1024 * 1024),
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await adminApi.delete(`/webmail/domains/${id}`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                message.success('删除成功');
                fetchDomains(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                ...values,
                cluster_id: selectedCluster,
                quota_bytes: values.quota_bytes_gb ? values.quota_bytes_gb * 1024 * 1024 * 1024 : values.quota_bytes,
            };
            delete data.quota_bytes_gb;

            if (editingDomain) {
                const res = await adminApi.put(`/webmail/domains/${editingDomain.id}`, data);
                if (res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchDomains(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '更新失败');
                }
            } else {
                const res = await adminApi.post('/webmail/domains', data);
                if (res.data.code === 0) {
                    message.success('创建成功');
                    setModalVisible(false);
                    fetchDomains(pagination.current, pagination.pageSize);
                } else {
                    message.error(res.data.message || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            const res = await adminApi.post(`/webmail/domains/${record.id}/toggle-status`, {
                cluster_id: selectedCluster,
            });
            if (res.data.code === 0) {
                message.success(res.data.message);
                fetchDomains(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    // Fetch sync status for all domains
    const fetchSyncStatus = async (domainIds) => {
        const statusMap = {};
        for (const id of domainIds) {
            try {
                const res = await adminApi.get(`/webmail/domains/${id}/sync-status`, {
                    params: { cluster_id: selectedCluster }
                });
                if (res.data.code === 0) {
                    statusMap[id] = res.data.data;
                }
            } catch (error) {
                // ignore
            }
        }
        setSyncStatus(statusMap);
    };

    // Fetch users for sync modal
    const fetchUsers = async (search = '') => {
        setUsersLoading(true);
        try {
            const res = await adminApi.get('/users', {
                params: { search, per_page: 50 }
            });
            if (res.data.code === 0 || res.data.success) {
                setUsers(res.data.data?.list || res.data.data || []);
            }
        } catch (error) {
            message.error('获取用户列表失败');
        }
        setUsersLoading(false);
    };

    // Open sync modal
    const handleOpenSync = (record) => {
        setSyncingDomain(record);
        syncForm.resetFields();
        // If already synced, pre-fill the user
        if (syncStatus[record.id]?.synced) {
            syncForm.setFieldsValue({ user_id: syncStatus[record.id].user_id });
        }
        fetchUsers();
        setSyncModalVisible(true);
    };

    // Sync domain to user
    const handleSync = async (values) => {
        if (!syncingDomain) return;
        setSyncSubmitting(true);
        try {
            const res = await adminApi.post(`/webmail/domains/${syncingDomain.id}/sync`, {
                cluster_id: selectedCluster,
                user_id: values.user_id,
            });
            if (res.data.code === 0) {
                message.success(res.data.message);
                setSyncModalVisible(false);
                // Refresh sync status
                fetchSyncStatus([syncingDomain.id]);
            } else {
                message.error(res.data.message || '同步失败');
            }
        } catch (error) {
            message.error('同步失败');
        } finally {
            setSyncSubmitting(false);
        }
    };

    // Unsync domain
    const handleUnsync = async (domainId) => {
        try {
            const res = await adminApi.post(`/webmail/domains/${domainId}/unsync`, {
                cluster_id: selectedCluster,
            });
            if (res.data.code === 0) {
                message.success(res.data.message);
                // Clear sync status
                setSyncStatus(prev => ({ ...prev, [domainId]: { synced: false } }));
            } else {
                message.error(res.data.message || '取消同步失败');
            }
        } catch (error) {
            message.error('取消同步失败');
        }
    };

    // Fetch sync status when domains change
    useEffect(() => {
        if (domains.length > 0) {
            fetchSyncStatus(domains.map(d => d.id));
        }
    }, [domains]);

    // View DNS records for a domain
    const handleViewDns = async (record) => {
        setDnsDomain(record);
        setDnsRecords([]);
        setDnsModalVisible(true);
        setDnsLoading(true);
        try {
            const res = await adminApi.get(`/webmail/domains/${record.id}/dns`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                const data = res.data.data || {};
                setDnsRecords(data.records || data.list || (Array.isArray(data) ? data : []));
            } else {
                message.error(res.data.message || '获取 DNS 记录失败');
            }
        } catch (error) {
            message.error('获取 DNS 记录失败');
        }
        setDnsLoading(false);
    };

    // Generate DKIM key for the current DNS domain
    const handleGenerateDkim = async () => {
        if (!dnsDomain) return;
        setDkimLoading(true);
        try {
            const res = await adminApi.post(`/webmail/domains/${dnsDomain.id}/dkim`, {
                cluster_id: selectedCluster,
            });
            if (res.data.code === 0) {
                message.success('DKIM 密钥已生成，请刷新 DNS 记录');
                handleViewDns(dnsDomain);
            } else {
                message.error(res.data.message || 'DKIM 生成失败');
            }
        } catch (error) {
            message.error('DKIM 生成失败');
        }
        setDkimLoading(false);
    };

    // View usage stats for a domain
    const handleViewStats = async (record) => {
        setStatsDomain(record);
        setDomainStats(null);
        setStatsModalVisible(true);
        setStatsLoading(true);
        try {
            const res = await adminApi.get(`/webmail/domains/${record.id}/stats`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                setDomainStats(res.data.data || {});
            } else {
                message.error(res.data.message || '获取统计失败');
            }
        } catch (error) {
            message.error('获取统计失败');
        }
        setStatsLoading(false);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '域名',
            dataIndex: 'name',
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: '状态',
            dataIndex: 'active',
            width: 80,
            render: (active, record) => (
                <Switch
                    checked={active}
                    size="small"
                    onChange={() => handleToggleStatus(record)}
                />
            ),
        },
        {
            title: '用户数',
            width: 120,
            render: (_, record) => (
                <Tooltip title={`${record.current_users} / ${record.max_users}`}>
                    <Progress
                        percent={Math.round((record.current_users / record.max_users) * 100)}
                        size="small"
                        format={() => `${record.current_users}/${record.max_users}`}
                    />
                </Tooltip>
            ),
        },
        {
            title: '配额使用',
            width: 150,
            render: (_, record) => (
                <Tooltip title={`${formatBytes(record.used_bytes)} / ${formatBytes(record.quota_bytes)}`}>
                    <Progress
                        percent={Math.round((record.used_bytes / record.quota_bytes) * 100)}
                        size="small"
                        status={record.used_bytes / record.quota_bytes > 0.9 ? 'exception' : 'normal'}
                    />
                </Tooltip>
            ),
        },
        {
            title: '邮件数',
            width: 120,
            render: (_, record) => (
                <span>{record.used_messages?.toLocaleString() || 0}</span>
            ),
        },
        {
            title: '同步状态',
            width: 140,
            render: (_, record) => {
                const status = syncStatus[record.id];
                if (!status) return <Tag>加载中...</Tag>;
                if (status.synced) {
                    return (
                        <Tooltip title={`归属: ${status.user_email || status.user_name || 'N/A'}`}>
                            <Tag color="green" icon={<LinkOutlined />}>
                                已同步
                            </Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="default">未同步</Tag>;
            },
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 260,
            render: (_, record) => {
                const status = syncStatus[record.id];
                return (
                    <Space>
                        <Tooltip title="管理用户">
                            <Button
                                size="small"
                                icon={<TeamOutlined />}
                                onClick={() => navigate(adminPath(`/webmail/users?domain_id=${record.id}&cluster_id=${selectedCluster}&domain_name=${record.name}`))}
                            />
                        </Tooltip>
                        <Tooltip title="DNS / DKIM">
                            <Button
                                size="small"
                                icon={<KeyOutlined />}
                                onClick={() => handleViewDns(record)}
                            />
                        </Tooltip>
                        <Tooltip title="用量统计">
                            <Button
                                size="small"
                                icon={<BarChartOutlined />}
                                onClick={() => handleViewStats(record)}
                            />
                        </Tooltip>
                        {status?.synced ? (
                            <>
                                <Tooltip title="修改归属">
                                    <Button size="small" icon={<SyncOutlined />} onClick={() => handleOpenSync(record)} />
                                </Tooltip>
                                <Popconfirm
                                    title="取消同步？"
                                    description="取消后用户将无法在前台管理此域名"
                                    onConfirm={() => handleUnsync(record.id)}
                                >
                                    <Button size="small" icon={<DisconnectOutlined />} />
                                </Popconfirm>
                            </>
                        ) : (
                            <Tooltip title="同步到用户">
                                <Button size="small" type="primary" ghost icon={<SyncOutlined />} onClick={() => handleOpenSync(record)} />
                            </Tooltip>
                        )}
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                        <Popconfirm
                            title="确定删除此域名吗？"
                            description="删除后域名下的所有用户将无法访问"
                            onConfirm={() => handleDelete(record.id)}
                        >
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <Card
            title="邮件域名管理"
            extra={
                <Space>
                    <Select
                        style={{ width: 180 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={() => fetchDomains(pagination.current, pagination.pageSize)} />
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加域名
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={domains}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedCluster ? '暂无域名，点击右上角“添加域名”创建' : '请先选择集群'} /> }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: (page, pageSize) => fetchDomains(page, pageSize),
                }}
            />

            <Modal
                title={editingDomain ? '编辑域名' : '添加域名'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editingDomain && (
                        <Form.Item
                            name="name"
                            label="域名"
                            rules={[{ required: true, message: '请输入域名' }]}
                        >
                            <Input placeholder="如：example.com" />
                        </Form.Item>
                    )}
                    <Form.Item name="quota_bytes_gb" label="存储配额 (GB)">
                        <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="quota_messages" label="邮件数量限制">
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="max_users" label="最大用户数">
                        <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="active" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="禁用" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Sync Modal */}
            <Modal
                title={`同步域名: ${syncingDomain?.name || ''}`}
                open={syncModalVisible}
                onCancel={() => setSyncModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form form={syncForm} layout="vertical" onFinish={handleSync}>
                    <Form.Item
                        name="user_id"
                        label="选择用户"
                        rules={[{ required: true, message: '请选择用户' }]}
                        extra="将此域名分配给选定的用户，用户可在前台管理此域名的邮箱"
                    >
                        <Select
                            showSearch
                            placeholder="搜索并选择用户"
                            filterOption={false}
                            onSearch={(value) => fetchUsers(value)}
                            loading={usersLoading}
                            optionFilterProp="children"
                        >
                            {users.map(u => (
                                <Select.Option key={u.id} value={u.id}>
                                    {u.email} ({u.username || u.id})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    {syncStatus[syncingDomain?.id]?.synced && (
                        <div style={{ marginBottom: 16, padding: 8, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                            <strong>当前归属:</strong> {syncStatus[syncingDomain?.id]?.user_email || 'N/A'}
                        </div>
                    )}
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={syncSubmitting}>
                                {syncStatus[syncingDomain?.id]?.synced ? '更新归属' : '同步'}
                            </Button>
                            <Button onClick={() => setSyncModalVisible(false)} disabled={syncSubmitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* DNS / DKIM Modal */}
            <Modal
                title={`DNS 记录 - ${dnsDomain?.name || ''}`}
                open={dnsModalVisible}
                onCancel={() => setDnsModalVisible(false)}
                width={820}
                footer={[
                    <Popconfirm
                        key="dkim"
                        title="生成 / 重新生成 DKIM"
                        description="重新生成后需要更新 DNS 记录才能继续收发邮件"
                        okText="确认"
                        cancelText="取消"
                        onConfirm={handleGenerateDkim}
                    >
                        <Button icon={<SafetyCertificateOutlined />} loading={dkimLoading}>
                            生成 DKIM
                        </Button>
                    </Popconfirm>,
                    <Button key="refresh" icon={<ReloadOutlined />} onClick={() => dnsDomain && handleViewDns(dnsDomain)}>
                        刷新
                    </Button>,
                    <Button key="close" onClick={() => setDnsModalVisible(false)}>关闭</Button>,
                ]}
            >
                <Table
                    dataSource={dnsRecords}
                    rowKey={(r, i) => i}
                    loading={dnsLoading}
                    pagination={false}
                    size="small"
                    locale={{ emptyText: '暂无 DNS 记录' }}
                    columns={[
                        { title: '类型', dataIndex: 'type', width: 70, render: (t) => <Tag>{t}</Tag> },
                        { title: '用途', dataIndex: 'purpose', width: 160, render: (v, r) => v || r.name_hint || '-' },
                        {
                            title: '主机记录',
                            dataIndex: 'name',
                            width: 200,
                            render: (name) => (
                                <Typography.Paragraph copyable={{ text: name }} style={{ margin: 0, fontSize: 12 }}>
                                    {name}
                                </Typography.Paragraph>
                            ),
                        },
                        {
                            title: '记录值',
                            dataIndex: 'value',
                            render: (value, record) => (
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Typography.Paragraph
                                        copyable={{ text: value }}
                                        style={{ margin: 0, wordBreak: 'break-all', fontSize: 12 }}
                                        ellipsis={{ rows: 2, expandable: true }}
                                    >
                                        {value}
                                    </Typography.Paragraph>
                                    {record.priority ? (
                                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>Priority: {record.priority}</Typography.Text>
                                    ) : null}
                                </Space>
                            ),
                        },
                    ]}
                />
            </Modal>

            {/* Domain Stats Modal */}
            <Modal
                title={`用量统计 - ${statsDomain?.name || ''}`}
                open={statsModalVisible}
                onCancel={() => setStatsModalVisible(false)}
                width={640}
                footer={[
                    <Button key="refresh" icon={<ReloadOutlined />} onClick={() => statsDomain && handleViewStats(statsDomain)}>
                        刷新
                    </Button>,
                    <Button key="close" onClick={() => setStatsModalVisible(false)}>关闭</Button>,
                ]}
            >
                <Spin spinning={statsLoading}>
                    {domainStats ? (
                        <>
                            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="用户数" value={domainStats.total_users ?? domainStats.current_users ?? statsDomain?.current_users ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="存储使用" value={formatBytes(domainStats.used_bytes ?? statsDomain?.used_bytes ?? 0)} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="邮件总数" value={domainStats.used_messages ?? domainStats.total_messages ?? statsDomain?.used_messages ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日发送" value={domainStats.today_sent ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日接收" value={domainStats.today_received ?? 0} />
                                    </Card>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <Card size="small">
                                        <Statistic title="今日失败" value={domainStats.today_failed ?? 0} valueStyle={{ color: (domainStats.today_failed || 0) > 0 ? '#cf1322' : undefined }} />
                                    </Card>
                                </Col>
                            </Row>
                            <Descriptions bordered column={1} size="small" style={{ marginTop: 12 }}>
                                <Descriptions.Item label="存储配额">{formatBytes(domainStats.quota_bytes ?? statsDomain?.quota_bytes ?? 0)}</Descriptions.Item>
                                <Descriptions.Item label="邮件数配额">{(domainStats.quota_messages ?? statsDomain?.quota_messages ?? 0).toLocaleString()}</Descriptions.Item>
                                <Descriptions.Item label="最大用户数">{domainStats.max_users ?? statsDomain?.max_users ?? '-'}</Descriptions.Item>
                            </Descriptions>
                        </>
                    ) : (
                        !statsLoading && <Empty description="暂无统计数据" />
                    )}
                </Spin>
            </Modal>
        </Card>
    );
};

export default Domains;
